import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import { Modal as SemanticModal } from "semantic-ui-react"; // todo: can we use our modal?

import Button from "@cayuse/shared-react.components.button";
import ButtonGroup from "@cayuse/shared-react.components.button-group";
import Radio from "@cayuse/shared-react.components.form.inputs.radio";
import Modal from "@cayuse/shared-react.components.modal";
import { isNonEmptyArray, isNonEmptyObject, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import SearchBar from "components/SearchBar";
import SectionPicker from "components/StartNewAward/SectionPicker";
import {
	AWARDS_BASE_ROUTE,
	COLUMN_HEADER_KEYS,
	ERROR_NOT_AUTHORIZED,
	ERROR_STATUS_CODE_400,
	PRIVILEGES,
	START_AWARD_FROM_AWARD,
	START_AWARD_FROM_PROPOSAL,
} from "globals/constants";
import { useHasPrivileges, useDictionary } from "hooks";
import { refreshAwards } from "store/actions/awardList";
import { refreshProjects } from "store/actions/projectList";
import { refreshProposals } from "store/actions/proposals";
import { fetchShell } from "store/actions/shell";
import { tenantSelectors } from "store/selectors";
import {
	createFromOptions,
	debounce,
	getTotalProjects as queryTotalProjects,
	notifyError,
	notifySuccess,
	spUrl,
	validateTitleLength,
	trapFocusInsideNotification
} from "utils";
import history from "utils/history";

import styles from "./styles.css";
import { api } from "services";
import {IncompatibleWarning} from "../FormResponse/IncompatibleWarning";
import {toast} from "react-toastify";

const MODAL_ID = "newAwardModal";

const StartNewAward = ({ fromProject = false, projectId = null }) => {
	const actions = {
		create: "create",
		fromSource: "from source",
		fromProposal: "from proposal",
		fromAward: "from award"
	};

	const defaults = {
		action: undefined,
		visible: false,
		open: false,
		transitionDuration: 150,
		titleField: "",
		searchField: undefined,
		searchInput: "",
		confirmDisabled: true,
		searchQuery: ""
	};

	const dictionary = useDictionary("awards.startNewAward");

	const [ isLoading, setIsLoading ] = useState(false);
	const [ activeStep, setActiveStep ] = useState("create from");
	const [ action, setAction ] = useState(defaults.action);
	const [ visible, setVisible ] = useState(defaults.visible);
	const [ open, setOpen ] = useState(defaults.open);
	const [ titleField, setTitleField ] = useState(defaults.titleField);
	const [ searchField, setSearchField ] = useState(defaults.searchField);
	const [ searchInput, setSearchInput ] = useState(defaults.searchInput);
	const [ parentType, setParentType ] = useState(null);
	const [ confirmDisabled, setConfirmDisabled ] = useState( defaults.confirmDisabled);
	const [ disabledReason, setDisabledReason ] = useState(null);

	const [ hasProjectOptions, setHasProjectOptions ] = useState(false);
	const [ proposalOptions, setProposalOptions ] = useState([]);
	const [ awardOptions, setAwardOptions ] = useState([]);
	const [ isFetchingOptions, setIsFetchingOptions ] = useState(false);
	const [ checkedValues, setCheckedValues ] = useState([]);
	const [ formElements, setFormElements ] = useState([]);

	const { tenantId } = useSelector(tenantSelectors.tenant);

	const isCreate = action === actions.create;

	const { [PRIVILEGES.AWARD_CREATE]: canCreateAwards } = useHasPrivileges([ PRIVILEGES.AWARD_CREATE ]);

	const ariaControlsAttr = open ? { [`aria-controls`]: MODAL_ID } : {};

	useEffect(() => {
		const errors = dictionary.errors || {};
		let reason = null;

		if (confirmDisabled) {
			if (!action) {
				reason = errors.noActionSelected || "Please select where to create a new proposal";
			} else if (action === actions.create) {
				reason = errors.noProjectTitle || "Please enter a project title";
			} else {
				reason = errors.noProjectSelected || "Please select a project";
			}
		}

		if (reason !== disabledReason) {
			setDisabledReason(reason);
		}
	}, [
		action,
		actions.create,
		confirmDisabled,
		dictionary.errors,
		disabledReason,
		searchField,
		titleField,
	]);

	const refreshData = () => {
		fetchShell();
		refreshProposals();
		refreshProjects();
		refreshAwards();
	};

	const onActionChange = value => {
		setSearchInput(defaults.searchInput);
		setAction(value);

		if (value === actions.create) {
			setConfirmDisabled(!titleField || !isNonEmptyString(titleField.trim()) || !validateTitleLength(titleField));
		} else if (value === actions.fromSource) {
			setConfirmDisabled(!searchField || !isNonEmptyString(searchField.trim()));
		}

		if (!visible) {
			setVisible(true);
		}
	};

	const addToManyResultsText = useCallback((options, totalElements) => {
		if(options?.length < totalElements) {
			return {
				key: "refine-results",
				value: "refine-results",
				text: `There are too many results for "${searchInput}", try adding to your search to see more specific results`,
				disabled: true
			};
		}

		return false;
	}, [ searchInput ]);

	useEffect(() => {
		setProposalOptions([]);
		setSearchField(defaults.searchField);
		if (isNonEmptyString(searchInput) && action === actions.fromProposal) {
			setIsFetchingOptions(true);

			createFromOptions({
				fields: START_AWARD_FROM_PROPOSAL.requiredFields,
				searchFields: START_AWARD_FROM_PROPOSAL.searchFields,
				searchQuery: searchInput,
				formType: COLUMN_HEADER_KEYS.PROPOSAL.toLowerCase(),
				fromProject,
				projectId
		})
				.then(results => {
					const updatedOptions = results?.content?.map((proposalItem, index) => {
						const principalInvestigator = proposalItem["principalInvestigator"] === undefined ? "" : ` -  ${proposalItem["principalInvestigator"]}`;
						const textString = `${proposalItem.proposalNumber} : ${proposalItem.projectTitle} ${principalInvestigator}`;

						return {
							content: textString,
							key: proposalItem.proposalId,
							value: proposalItem.proposalId,
							original: textString,
							text: `Result ${index + 1} of ${results?.content?.length} for ${searchInput}: ${textString}`,
							projectTitle: proposalItem.projectTitle,
						};
					});

					const toManyResultsText = addToManyResultsText(updatedOptions, results?.totalElements);

					if(toManyResultsText) {
						updatedOptions.push(toManyResultsText);
					}

					setProposalOptions(updatedOptions);
				})
				.finally(() => setIsFetchingOptions(false));
		}
	}, [ searchInput, action, actions.fromProposal, tenantId, addToManyResultsText ]);

	const loadAwardOptions = async () => {
		setIsFetchingOptions(true);
		try {
			const results = await api.awards.getAttributes({
				attributes: START_AWARD_FROM_AWARD.searchFields,
				requiredAttributes: START_AWARD_FROM_AWARD.requiredFields,
				search: searchInput,
				projectId: fromProject ? projectId : null,
			});

			const updatedOptions = results?.content?.map((awardItem, index) => {
				const principalInvestigator =
						awardItem["principalInvestigator"] === undefined ? "" : ` -  ${awardItem["principalInvestigator"]}`;
				const projectTitle =
						awardItem["projectTitle"] === undefined ? "Untitled" : ` ${awardItem["projectTitle"]}`;
				const textString = `${awardItem.awardNumber} : ${projectTitle} ${principalInvestigator}`;

				return {
					content: textString,
					key: awardItem.awardId,
					original: textString,
					projectTitle: awardItem.projectTitle,
					text: `Result ${index + 1} of ${results?.content?.length} for ${searchInput}: ${textString}`,
					value: awardItem.awardId,
				};
			});

			const toManyResultsText = addToManyResultsText(updatedOptions, results?.totalElements);

			if(toManyResultsText) {
				updatedOptions.push(toManyResultsText);
			}
			setAwardOptions(updatedOptions);

		} finally {
			setIsFetchingOptions(false);
		}
	}

	useEffect(() => {
		setAwardOptions([]);
		setSearchField(defaults.searchField);
		if (isNonEmptyString(searchInput) && action === actions.fromAward) {
			loadAwardOptions();
		}
	}, [ searchInput, action, actions.fromAward ]);

	const getTotalProjects = () => {
		queryTotalProjects().then(totalProjects => {
			setHasProjectOptions(totalProjects > 0);
		});
	};

	const resetModalValues = () => {
		getTotalProjects();
		setAction(fromProject ? actions.fromSource : defaults.action);
		setVisible(fromProject ? true : defaults.visible);
		setTitleField(defaults.titleField);
		setSearchField(defaults.searchField);
		setSearchInput(defaults.searchInput);
		setParentType(null);
		setOpen(true);
	};

	const onClose = () => {
		setOpen(false);
		setActiveStep("create from");

		const triggerButton = document.getElementById("startNewAwardBtn");

		if (triggerButton) {
			triggerButton.focus();
		}
	};

	const onSave = useCallback(async (payload, action) => {
		try {
			const response = await api.awards.create(payload, { notifyUponError: false });
			const award = response.submission;
			const failedElements = response.failedFormElements;

			if(failedElements && failedElements.length !== 0) {
				const lastActiveElement = document.activeElement;
				toast(<IncompatibleWarning elements={failedElements} />, {
					type: "warning",
					autoClose: false,
					onOpen: () => {
						trapFocusInsideNotification();
					},
					onClose: () => {
						setTimeout(() => {
							lastActiveElement.focus();
						}, 0);
					}
				});
			}

			refreshData();
			onClose();

			switch (action) {
			case actions.fromProposal:
				notifySuccess(dictionary.successMessages.fromProposal);
				break;
			case actions.fromAward:
				notifySuccess(dictionary.successMessages.fromAward);
				break;
			case actions.fromSource:
				notifySuccess(dictionary.successMessages.fromSource);
				break;
			case actions.create:
				notifySuccess(dictionary.successMessages.create);
				break;
			default:
				notifySuccess(dictionary.successMessages.fromSource);
				break;
			}
			history.push(spUrl(`${AWARDS_BASE_ROUTE}/${award.awardId}`));
		} catch (error) {
			console.error(error);
			if (error.response?.status === ERROR_STATUS_CODE_400) {
				notifyError(dictionary.errors.notAuthorized || ERROR_NOT_AUTHORIZED);
			} else {
				notifyError(dictionary.errors.default || "Failed to copy data from form");
			}
		} finally {
			setIsLoading(false);
		}
	}, [ dictionary.errors.default, dictionary.errors.notAuthorized ]);

	const onConfirmCreate = useCallback(action => {
		const payload = {
			projectTitle: titleField
		};
		onSave(payload, action);
	}, [ onSave, titleField ]);

	const onConfirmFromSource = useCallback(options => {
		const headerIds = checkedValues.reduce((headerIds, section) => {
			return [ ...headerIds, ...section.fieldHeaderIds ];
		}, []);
		const copySections = checkedValues.map(item => item.label).filter(section => section !== null);

		const duplicateHeaderIds = headerIds.filter((item, index) => headerIds.indexOf(item) !== index);
		const hasDuplicateHeaderIds = isNonEmptyArray(duplicateHeaderIds);
		const elementsWithDuplicateHeaderIds = formElements.filter(element => (
			duplicateHeaderIds.includes(element.headerId)
		));

		const formattedErrorMessage = () => {
			return (
				<>
					<p>
						{/* eslint-disable-next-line max-len */}
						{dictionary.errors.duplicateLabelsMsg ?? "Duplicate Labels - Unselect sections that contain duplicate labels to copy data from remaining selections.Remove duplicate Labels from the form to prevent this error from occurring on future forms."}
					</p>
					<div>

					</div>
					<ul className={styles.errorList}>
						{elementsWithDuplicateHeaderIds.map(element => {
							return (
								<li>
									<p><strong>{dictionary.errors.section ?? "Section"}:</strong> {element.section}</p>
									<p><strong>{dictionary.errors.question ?? "Question"}:</strong> {element.label}</p>
									<p><strong>{dictionary.errors.label ?? "Label"}:</strong> {element.headerId}</p>
								</li>
							);
						})}
					</ul>
				</>
			);
		};


		const selectedProjectTitle = options.find(
			({ key }) => searchField === key
		)?.projectTitle;

		const payload = {
			parentId: searchField,
			projectTitle: selectedProjectTitle,
			parentFormType: action === actions.fromProposal
				? COLUMN_HEADER_KEYS.PROPOSAL
				: COLUMN_HEADER_KEYS.AWARD,
			copyFields: headerIds.filter(headerId => headerId !== null),
			copySections,
		};

		if(hasDuplicateHeaderIds) {
			notifyError(formattedErrorMessage());
			setIsLoading(false);
		} else if (headerIds.some(headerId => headerId === null)) {
			notifyError(
				dictionary.errors.missingLabel ||
				"One or more labels are missing on the form you are trying to copy from. Copying from this form will not work",
			);
			setIsLoading(false);
		}
		else {
			onSave(payload, action);
		}
	}, [ action, actions.fromProposal, checkedValues, dictionary.errors.missingLabel, formElements, onSave, searchField ]);

	const onTitleChange = event => {
		const projectTitle = event.target.value;
		setConfirmDisabled(!projectTitle || !isNonEmptyString(projectTitle.trim()) || !validateTitleLength(projectTitle));
		setTitleField(projectTitle);
	};

	const onCreateFromTargetChange = (entityType, formInstanceId, options) => {
		// Used to change the display text of the selected item
		const updateDisplayText = () => {
			// find the selected option and restore the original value
			// const selected = _find(options, { value: value });
			const selected = options.find(option => option.key === formInstanceId);

			if(isNonEmptyObject(selected)) {
				selected.text = selected.original;
			}

			const updatedOptions = options.map(option => option.key === selected.key ? selected : option);

			// now set only one option in the list of results
			switch(entityType) {
			case "proposal":
				setProposalOptions(updatedOptions);
				break;
			case "award":
				setAwardOptions(updatedOptions);
				break;
			default:
				return false;
			}
		};

		if(isNonEmptyString(formInstanceId)) {
			updateDisplayText();
			setSearchField(formInstanceId);
			setConfirmDisabled(false);
		} else {
			setSearchInput(defaults.searchInput);
			setSearchField(defaults.searchField);
			setConfirmDisabled(defaults.confirmDisabled);
		}

		setParentType(entityType || null);
	};

	const onSearchChange = debounce(searchQuery => {
		setSearchInput(searchQuery);
	}, 500);

	const onConfirm = useCallback(() => {
		setIsLoading(true);
		if (action === actions.create) {
			onConfirmCreate(action);
		} else if (action === actions.fromProposal || action === actions.fromAward) {
			const options = action === actions.fromProposal
				? proposalOptions
				: awardOptions;
			onConfirmFromSource(options);
		} else {
			console.error("Not implemented: ", action);
		}
	}, [
		action,
		actions.create,
		actions.fromAward,
		actions.fromProposal,
		awardOptions,
		onConfirmCreate,
		onConfirmFromSource,
		proposalOptions,
	]);

	const renderHeader = useCallback(() => {
		switch(action) {
		case actions.fromProposal:
			return activeStep === "select sections"
				? dictionary.selectSections || "Select sections to copy"
				: dictionary.createNewAward || "Create New Award";
		case actions.create:
		case actions.fromSource:
		case actions.fromAward:
		default:
			return dictionary.createNewAward || "Create New Award";
		}
	}, [
		actions,
		action,
		activeStep,
		dictionary.createNewAward,
		dictionary.selectSections
	]);

	const renderActions = useCallback (() => {
		switch (action) {
		case actions.fromAward:
		case actions.fromProposal:
			return activeStep === "select sections" ? (
				<>
					<Button
						secondary
						onClick={() => setActiveStep("create from")}
						disabled={confirmDisabled}
						icon="arrow-left"
					>
						{dictionary.backButton || "Back"}
					</Button>

					<Button
						className={styles.confirmButton}
						onClick={onConfirm}
						disabled={confirmDisabled || isLoading}
						busy={isLoading}
						loadingMessage={dictionary.creatingAward || "Creating award"}
						tooltip={confirmDisabled && disabledReason}
					>
						{dictionary.createAwardButton || "Create Award"}
					</Button>
				</>
			) : (
				<Button
					primary
					onClick={() => setActiveStep("select sections")}
					disabled={confirmDisabled}
				>
					{dictionary.nextButton || "Next"}
				</Button>
			);
		case actions.create:
			return (
				<Button
					className={styles.confirmButton}
					onClick={onConfirm}
					disabled={confirmDisabled || isLoading}
					busy={isLoading}
					loadingMessage={dictionary.creatingProjectAndAward || "Creating project and award"}
					tooltip={confirmDisabled && disabledReason}
				>
					{dictionary.createProjectAndAwardButton || "Create Project and Award"}
				</Button>
			);
		default:
			return (
				<Button primary disabled={true}>
					{dictionary.nextButton || "Next"}
				</Button>
			);
		}
	}, [
		action,
		actions.create,
		actions.fromAward,
		actions.fromProposal,
		activeStep,
		confirmDisabled,
		dictionary.backButton,
		dictionary.creatingAward,
		dictionary.createAwardButton,
		dictionary.creatingProjectAndAward,
		dictionary.createProjectAndAwardButton,
		dictionary.nextButton,
		disabledReason,
		isLoading,
		onConfirm
	]);

	useEffect(() => {
		if (visible) {
			ReactTooltip.rebuild();
		}
	}, [ visible ]);

	return (
		<Modal
			id={MODAL_ID}
			className={styles.modal}
			closeIcon
			trigger={
				<Button
					icon="plus"
					primary
					disabled={!canCreateAwards}
					onClick={resetModalValues}
					aria-haspopup="dialog"
					{...ariaControlsAttr}
					id="startNewAwardBtn"
					aria-haspopup="dialog"
				>
					{ fromProject ? dictionary.addNew || "Add An Award" : dictionary.startNew || "Start New Award" }
				</Button>
			}
			open={open}
			onClose={onClose}
			size="one-half"
			ariaLabel={renderHeader()}
		>
			<SemanticModal.Header className="ui header whiteOnBlue">
				<h2 id="newAwardFormTitle">{renderHeader()}</h2>
			</SemanticModal.Header>
			<SemanticModal.Content>
				{activeStep === "create from" ? (
					<div className={`ui form ${styles.newAwardForm}`} role="group" aria-labelledby="newAwardFormTitle">
						{!fromProject && <>
							<Radio
								id={actions.create}
								name="actionGroup"
								checked={action === actions.create}
								value={actions.create}
								onChange={() => onActionChange(actions.create)}
							>
								<span dangerouslySetInnerHTML={{
									__html: dictionary.createNewProjectLabel || "<b>Create a new project</b> (create a new project and award at the same time)"
								}}
								/>
							</Radio>
							{visible && action === actions.create && (
								<div className={styles.dropdownWrapper}>
									<label htmlFor="newProjectNameInput" className={styles.label}>
										{dictionary.newLabel || "Name project"}
									</label>
									<input
										className={styles.modalInput}
										placeholder={dictionary.projectSearchPlaceholder || "Enter new project name..."}
										type="text"
										onChange={onTitleChange}
										value={titleField}
										id="newProjectNameInput"
									/>
									{!validateTitleLength(titleField) && <span className={styles.titleLengthWarning}>
										{dictionary.errors.projectTitleLengthValidation ?? "Project title must be less than 256 characters."}
									</span>}
								</div>
							)}
						</>}
						<Radio
							id={actions.fromSource}
							name="actionGroup"
							checked={action === actions.fromSource}
							value={actions.fromSource}
							onChange={() => onActionChange(actions.fromSource)}
							disabled={!hasProjectOptions}
						>
							<span dangerouslySetInnerHTML={{
								__html:
                                    dictionary.addToProjectLabel || "<b>Add to existing project</b> (create a new award on an existing project, copy an award or a proposal to create a new award)",
							}}
							/>
						</Radio>

						{visible && !isCreate && (
							<div className={styles.dropdownWrapper} role="group" aria-label="Add to existing project">
								<Radio
									id={actions.fromProposal}
									name="actionGroup"
									checked={action === actions.fromProposal}
									value={actions.fromProposal}
									onChange={() => onActionChange(actions.fromProposal)} /**/
								>
									<span
										dangerouslySetInnerHTML={{
											__html:
                        dictionary.createFromProposalLabel ||
                        "<b>Create award from a proposal </b> (award will be added to the same project as proposal)",
										}}
									/>
								</Radio>
								{visible && action === actions.fromProposal && (
									<>
										<div className={styles.label}>
											{dictionary.proposalSearchLabel || "Select proposal"}
										</div>
										<div
											className={`${styles.dropdownWrapper} ${styles.nested}`}
										>
											<SearchBar
												className={`${styles.modalInput} ${
													isFetchingOptions ? "loading" : ""
												}`}
												options={proposalOptions}
												onChange={(event, { value }) =>
													onCreateFromTargetChange("proposal", value, proposalOptions)
												}
												onSearchChange={(event, { searchQuery }) =>
													onSearchChange(searchQuery)
												}
												placeholder={
													dictionary.projectSearchProposalPlaceholder || "Click to search ..."
												}
												value={searchField}
											/>
										</div>
									</>
								)}
								<Radio
									id={actions.fromAward}
									name="actionGroup"
									checked={action === actions.fromAward}
									value={actions.fromAward}
									onChange={() => onActionChange(actions.fromAward)}
								>
									<span dangerouslySetInnerHTML={{
										__html:
                                            dictionary.createFromAwardLabel || "<b>Create award from an award </b> (new award will be added to the same project as the original award)",
									}}
									/>
								</Radio>
								{visible && action === actions.fromAward && (
									<>
										<div className={styles.label}>
											{dictionary.searchLabel || "Select award"}
										</div>
										<div className={`${styles.dropdownWrapper} ${styles.nested}`}>
											<SearchBar
												className={`${styles.modalInput} ${isFetchingOptions ? "loading" : ""}`}
												options={awardOptions}
												onChange={(event, { value }) => onCreateFromTargetChange("award", value, awardOptions)}
												onSearchChange={(event, { searchQuery }) => onSearchChange(searchQuery)}
												placeholder={dictionary.projectSearchAwardPlaceholder || "Click to search ..."}
												value={searchField}
											/>
										</div>
									</>
								)}
							</div>
						)}
					</div>
				) : (
					<SectionPicker
						parentId={searchField}
						parentType={parentType}
						headerText="All selected sections will be copied to the new award" /* todo: needs to go to the modal and into the dictionary */
						checkedValues={checkedValues}
						setFormElements={setFormElements}
						setCheckedValues={setCheckedValues}
					/>
				)}
			</SemanticModal.Content>
			<SemanticModal.Actions>
				<div className="field">
					<ButtonGroup>
						<Button secondary onClick={onClose}>
							{dictionary.cancel || "Cancel"}
						</Button>
						{renderActions()}
					</ButtonGroup>
				</div>
			</SemanticModal.Actions>
		</Modal>
	);
};

export default StartNewAward;
